import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ExternalLink, ArrowLeft } from "lucide-react";
import { useCSuiteAuth } from "../../state/CSuiteAuthContext";
import { LinkToThisText } from "./GrowthCopy";
import { AdminAiGuides } from "./AdminAiGuides";

const LISTINGS = [
  ["Software review sites", [
    ["G2", "https://sell.g2.com/", "Highest-authority B2B review site. Create a free profile, then ask 5 customers for reviews."],
    ["Capterra / GetApp / Software Advice", "https://www.capterra.com/vendors/sign-up", "One Gartner Digital Markets listing covers all three."],
    ["TrustRadius", "https://www.trustradius.com/vendors", "Strong with enterprise buyers."],
    ["SourceForge", "https://sourceforge.net/software/vendors/", "Free business software directory listing."],
    ["SaaSHub", "https://www.saashub.com/", "Free listing; add Capittance as an alternative to Jira, Smartsheet and MS Project."],
    ["AlternativeTo", "https://alternativeto.net/", "List Capittance as an alternative to Microsoft Project and Smartsheet."],
  ]],
  ["Launch and startup directories", [
    ["Product Hunt", "https://www.producthunt.com/", "Launch on a Tuesday–Thursday with the free tools as the hook."],
    ["Crunchbase", "https://www.crunchbase.com/", "Company profile; often cited by journalists."],
    ["BetaList", "https://betalist.com/", "Startup listing with a do-follow link."],
    ["Indie Hackers", "https://www.indiehackers.com/products", "Product page and build-in-public posts."],
  ]],
  ["ERP and PMO communities", [
    ["SAP Community blogs", "https://community.sap.com/", "Publish the cutover guide as a blog post with a link to the runbook."],
    ["Oracle Cloud Customer Connect", "https://community.oracle.com/customerconnect/", "Share the Oracle cutover checklist in the ERP forums."],
    ["ASUG / UKISUG / DSAG", "https://www.asug.com/", "SAP user groups: offer a talk on go-live readiness; speaker pages link back."],
    ["PMI chapters", "https://www.pmi.org/membership/chapters", "Offer a webinar on RAID logs and portfolio reporting."],
    ["LinkedIn company page", "https://www.linkedin.com/company/setup/new/", "Post one guide a week from the company page and your own profile."],
  ]],
];

const PITCHES = [
  ["Guest post pitch (ERP blogs and SI partner blogs)", `Subject: Guest article idea: the 3 Go/No-Go checkpoints that save ERP go-lives

Hi {first name},

I run Capittance, a testing and cutover control tower for SAP, Oracle and Workday programmes. I read your piece on {their article} and had an idea for your readers:

"The 3 Go/No-Go checkpoints that save ERP go-lives" — a practical 1,200-word article with the exact criteria programme leaders use at T-72h, T-24h and T-2h, plus a free 214-step runbook readers can download.

No sales pitch in the article; one link to the free template in the author bio. Happy to adapt the angle to your audience.

Would that be useful?

{your name}
Capittance · https://capittance.com`],
  ["Resource page link request", `Subject: A free tool for your ERP resources page

Hi {first name},

I noticed your resources page lists {tool or template they link to}. We built a free go-live readiness calculator for SAP, Oracle and Workday programmes — 12 weighted checks, no sign-up:
https://capittance.com/tools/go-live-readiness-calculator

If you think it helps your readers, we'd be grateful for a mention. Happy to share anything of yours with our audience in return.

Thanks,
{your name}`],
  ["Podcast / webinar pitch", `Subject: Episode idea: why ERP go-lives slip in the last 72 hours

Hi {first name},

I'd love to join {show name} to talk about what actually goes wrong in the last 72 hours before an ERP go-live — data reconciliation, interface switch-overs and Go/No-Go decisions made on optimism. I can bring real checklists listeners can download for free.

{your name}
Capittance · https://capittance.com`],
];

function Pitch({ title, body, i }) {
  return (
    <div className="p-5 rounded-md bg-white border" style={{ borderColor: "#F0D3D8" }} data-testid={`growth-pitch-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-[#0F1723]">{title}</div>
        <LinkToThisText text={body} testid={`growth-pitch-copy-${i}`} />
      </div>
      <pre className="mt-3 whitespace-pre-wrap text-xs text-zinc-700 font-sans leading-relaxed">{body}</pre>
    </div>
  );
}

export default function AdminGrowth() {
  const { user, loading } = useCSuiteAuth();
  if (loading || user === null) return null;
  if (!user || user.role !== "ADMIN") return <Navigate to="/csuite" replace />;
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
    <main className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-growth">
      <Link to="/csuite/dashboard" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"><ArrowLeft size={14}/> Dashboard</Link>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-4 text-[#4A0F1A]">Backlink outreach pack</h1>
      <p className="mt-2 text-sm text-zinc-700 max-w-3xl">Backlinks come from other websites linking to yours. Work through the listings first (one afternoon), then send 5 pitches a week. Your best link magnets are the free tools, the 214-step runbook and the guides.</p>
      <AdminAiGuides />
      {LISTINGS.map(([group, items]) => (
        <section key={group} className="mt-8">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>{group}</h2>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {items.map(([name, url, tip]) => (
              <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="p-4 rounded-md bg-white border hover:border-zinc-500 transition-colors" style={{ borderColor: "#F0D3D8" }} data-testid={`growth-listing-${name.split(" ")[0].toLowerCase()}`}>
                <div className="font-semibold text-[#0F1723] flex items-center gap-1.5">{name} <ExternalLink size={12}/></div>
                <p className="text-xs text-zinc-600 mt-1">{tip}</p>
              </a>
            ))}
          </div>
        </section>
      ))}
      <section className="mt-10">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Ready-to-send pitches</h2>
        <div className="mt-3 space-y-3">{PITCHES.map(([t, b], i) => <Pitch key={t} title={t} body={b} i={i} />)}</div>
      </section>
      <section className="mt-10 p-5 rounded-md bg-white border" style={{ borderColor: "#F0D3D8" }}>
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Pages to promote</h2>
        <ul className="mt-2 text-sm space-y-1 text-zinc-800">
          {["/tools/go-live-readiness-calculator", "/tools/cutover-timeline-builder", "/guides/erp-cutover-plan", "/guides/erp-go-live-checklist", "/compare"].map((p) => <li key={p}><a href={p} className="underline">capittance.com{p}</a></li>)}
        </ul>
      </section>
    </main>
    </div>
  );
}
